// Iter 62bp — Client Portal (public, no login). Magic link per client.
// Route: /portal/:token — read-only view showing videos + statuses + next ETA.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_META = {
  delivered:   { label: 'Livrées',       color: '#34D399', icon: '✓' },
  review:      { label: 'En validation', color: '#F5A623', icon: '👁' },
  in_progress: { label: 'En production', color: '#5BBEFC', icon: '🎬' },
  upcoming:    { label: 'À venir',       color: '#A78BFA', icon: '📅' },
};

export default function ClientPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/portal/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setError('Lien invalide ou expiré.'));
  }, [token]);

  if (error) return <PortalError message={error} />;
  if (!data) return <PortalLoading />;

  const { client, counts, buckets, next_eta } = data;
  const initials = (client.name || '?').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div style={{
      minHeight: '100vh', paddingBottom: 60,
      background: 'radial-gradient(1200px 800px at 10% -10%, rgba(91,190,252,0.08), transparent 60%), #050912',
      color: '#EEF1FB', fontFamily: 'Sora, system-ui',
    }}>
      {/* Header */}
      <header style={{ padding: '32px 24px 20px', maxWidth: 960, margin: '0 auto' }}>
        <div style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.16em',
          color: '#5BBEFC', textTransform: 'uppercase', marginBottom: 8,
        }}>Portail client · ASA by Elevate Influence</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {client.avatar ? (
            <img src={client.avatar} alt="" width={64} height={64}
              style={{ borderRadius: 16, objectFit: 'cover', boxShadow: `0 0 0 2px ${client.color || '#5BBEFC'}55` }} />
          ) : (
            <div style={{
              width: 64, height: 64, borderRadius: 16,
              background: `linear-gradient(135deg, ${client.color || '#5BBEFC'}, ${client.color || '#5BBEFC'}66)`,
              display: 'grid', placeItems: 'center',
              fontSize: 22, fontWeight: 800, color: '#0B1330',
            }}>{initials}</div>
          )}
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em' }}>{client.name}</h1>
            <div style={{ fontSize: 13, color: '#8A9DB8', marginTop: 4 }}>
              {counts.total} contenu{counts.total > 1 ? 's' : ''} suivi{counts.total > 1 ? 's' : ''}
              {next_eta && ` · prochaine livraison ${formatDate(next_eta)}`}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div style={{
          marginTop: 24, display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10,
        }}>
          {Object.entries(STATUS_META).map(([k, m]) => (
            <div key={k} style={{
              padding: 14, borderRadius: 14,
              background: `radial-gradient(120% 100% at 100% 0%, ${m.color}20 0%, transparent 55%), rgba(255,255,255,0.02)`,
              border: `1.5px solid ${m.color}33`,
            }}>
              <div style={{ fontSize: 9.5, fontWeight: 800, color: m.color, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{m.label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{counts[k] || 0}</div>
            </div>
          ))}
        </div>
      </header>

      {/* Buckets */}
      <main style={{ maxWidth: 960, margin: '0 auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 28 }}>
        {['delivered', 'review', 'in_progress', 'upcoming'].map((key) => {
          const items = buckets[key] || [];
          if (items.length === 0) return null;
          const m = STATUS_META[key];
          return (
            <section key={key}>
              <h2 style={{
                margin: '0 0 12px', fontSize: 16, fontWeight: 800,
                paddingBottom: 8, borderBottom: `1.5px solid ${m.color}33`,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ color: m.color }}>{m.icon}</span>
                {m.label}
                <span style={{ fontSize: 11, fontWeight: 700, color: m.color, opacity: 0.7 }}>{items.length}</span>
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map((it) => <ItemRow key={it.id} item={it} accent={m.color} />)}
              </div>
            </section>
          );
        })}
        {counts.total === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#565F84', fontStyle: 'italic' }}>
            Aucun contenu à afficher pour l&apos;instant.
          </div>
        )}
      </main>

      <footer style={{ textAlign: 'center', padding: 24, fontSize: 11, color: '#565F84' }}>
        ASA · propulsé par Elevate Influence
      </footer>
    </div>
  );
}

function ItemRow({ item, accent }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: 12, borderRadius: 12,
      background: 'rgba(255,255,255,0.02)',
      border: `1px solid ${item.overdue ? '#F0616B44' : 'rgba(255,255,255,0.06)'}`,
    }}>
      {item.thumbnail ? (
        <img src={item.thumbnail} alt="" width={56} height={56}
          style={{ borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
      ) : (
        <div style={{
          width: 56, height: 56, borderRadius: 10,
          background: `${accent}18`, border: `1px solid ${accent}44`,
          display: 'grid', placeItems: 'center', flexShrink: 0,
          fontSize: 20, color: accent,
        }}>🎬</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
        <div style={{ fontSize: 11, color: '#8A9DB8', marginTop: 4 }}>
          {item.format && `${item.format} · `}
          {item.delivered_at
            ? `Livré ${formatDate(item.delivered_at)}`
            : item.due_date
              ? `Prévu ${formatDate(item.due_date)}${item.overdue ? ' · en retard' : ''}`
              : 'Sans date'}
        </div>
      </div>
      {item.video_url && (
        <a href={item.video_url} target="_blank" rel="noreferrer noopener"
          style={{
            padding: '6px 12px', borderRadius: 8,
            background: `linear-gradient(135deg, ${accent}, ${accent}aa)`,
            color: '#0B1330', textDecoration: 'none',
            fontSize: 11, fontWeight: 800,
          }}
        >Voir ↗</a>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) { return iso; }
}

function PortalLoading() {
  return (
    <div style={{ minHeight: '100vh', background: '#050912', color: '#8A9DB8', display: 'grid', placeItems: 'center', fontFamily: 'Sora' }}>
      Chargement…
    </div>
  );
}

function PortalError({ message }) {
  return (
    <div style={{
      minHeight: '100vh', background: '#050912', color: '#EEF1FB',
      display: 'grid', placeItems: 'center', padding: 24, fontFamily: 'Sora',
    }}>
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ fontSize: 42, marginBottom: 12 }}>🔒</div>
        <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>Portail inaccessible</h1>
        <p style={{ color: '#8A9DB8', fontSize: 13 }}>{message}</p>
        <p style={{ color: '#565F84', fontSize: 12, marginTop: 20 }}>
          Contacte ton studio pour obtenir un nouveau lien.
        </p>
      </div>
    </div>
  );
}
